import { useEffect, useRef, useState } from "react";

import {
  MONOLITH_BOARD_MODAL_EYEBROW_CLASS,
  MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS,
  MONOLITH_BOARD_MODAL_INPUT_CLASS,
  MONOLITH_BOARD_MODAL_TITLE_CLASS,
  MonolithBoardModal,
  MonolithBoardModalSteps,
  type MonolithBoardModalStep
} from "@/components/dashboard/MonolithBoardModal";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/classNames";

export interface OnboardingValues {
  campaignName: string;
  displayName: string;
  email: string;
  password: string;
}

type FlowView = "choice" | "sign-in" | "sign-up";

interface OnboardingModalProps {
  open: boolean;
  submitting: boolean;
  phase?: "collect" | "setup";
  initialValues?: Partial<OnboardingValues>;
  setupError?: string | null;
  onClose: () => void;
  onSignIn: (email: string, password: string) => Promise<void>;
  onSubmit: (values: OnboardingValues) => Promise<void>;
  onRetrySetup?: () => void;
  onDismissSetup?: () => void;
}

const SIGN_UP_STEPS = [
  {
    key: "account",
    title: "Create your account",
    description: "Sign up with an email and password. Next you will name your campaign.",
    label: "",
    placeholder: "",
  },
  {
    key: "campaignName",
    title: "Name the campaign",
    description: "This campaign will hold the map you are building right now.",
    label: "Campaign name",
    placeholder: "The Shattered Wilds",
  },
  {
    key: "displayName",
    title: "Choose your username",
    description: "This uses the existing display name field and will identify you in the app.",
    label: "Username",
    placeholder: "hexmancer42",
  },
] as const;

type StepKey = (typeof SIGN_UP_STEPS)[number]["key"];

const STEP_LABELS: Record<StepKey, string> = {
  account: "Account",
  campaignName: "Campaign",
  displayName: "Username",
};

function getInitialValues(initialValues?: Partial<OnboardingValues>): OnboardingValues {
  return {
    campaignName: initialValues?.campaignName ?? "",
    displayName: initialValues?.displayName ?? "",
    email: initialValues?.email ?? "",
    password: "",
  };
}

export default function OnboardingModal({
  open,
  submitting,
  phase = "collect",
  initialValues,
  setupError = null,
  onClose,
  onSignIn,
  onSubmit,
  onRetrySetup,
  onDismissSetup,
}: OnboardingModalProps) {
  const wasOpenRef = useRef(open);
  const [view, setView] = useState<FlowView>("choice");
  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState<OnboardingValues>(() => getInitialValues(initialValues));
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");

  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;

    if (open && !wasOpen) {
      setValues(getInitialValues(initialValues));
      setStepIndex(0);
      setView("choice");
      setSignInEmail("");
      setSignInPassword("");
    }
  }, [initialValues, open]);

  const isSettingUpAccount = phase === "setup";
  const setupFailed = Boolean(setupError);
  const currentStep = SIGN_UP_STEPS[stepIndex];
  const isAccountStep = currentStep?.key === "account";
  const currentValue = isAccountStep ? values.email : values[currentStep?.key ?? "campaignName"];
  const stepNumber = stepIndex + 1;
  const canContinueSignUp = isAccountStep
    ? values.email.trim().length > 0 && values.password.length >= 6
    : currentValue.trim().length > 0;

  const canSubmitSignIn =
    signInEmail.trim().length > 0 && signInPassword.length > 0 && !submitting;

  const modalSteps: MonolithBoardModalStep[] = SIGN_UP_STEPS.map((step, index) => ({
    id: step.key,
    label: STEP_LABELS[step.key],
    status: index === stepIndex ? "current" : index < stepIndex ? "complete" : "upcoming",
    onSelect: () => setStepIndex(index),
  }));

  async function handleSignUpPrimaryAction() {
    if (!currentStep || !canContinueSignUp || submitting) {
      return;
    }

    if (stepIndex < SIGN_UP_STEPS.length - 1) {
      setStepIndex((index) => index + 1);
      return;
    }

    await onSubmit(values);
  }

  async function handleSignInSubmit() {
    if (!canSubmitSignIn) {
      return;
    }
    await onSignIn(signInEmail.trim(), signInPassword);
  }

  function handleModalClose() {
    if (phase === "setup") {
      return;
    }
    onClose();
  }

  return (
    <MonolithBoardModal
      open={open}
      titleId="onboarding-title"
      onClose={handleModalClose}
      submitting={submitting}
      dismissible={phase !== "setup"}
    >
      {isSettingUpAccount ? (
        <div className="flex flex-col" aria-live="polite">
          <p className={MONOLITH_BOARD_MODAL_EYEBROW_CLASS}>
            {setupFailed ? "Setup paused" : "Finalizing your workspace"}
          </p>
          <h2 id="onboarding-title" className={cn("mt-2", MONOLITH_BOARD_MODAL_TITLE_CLASS)}>
            {setupFailed ? "We couldn't finish setup." : "Setting up your account"}
          </h2>
          <div className="mt-8 flex items-center gap-4">
            {setupFailed ? (
              <div
                aria-hidden="true"
                className="flex h-12 w-12 shrink-0 items-center justify-center border-[3px] border-[#7a1d16] bg-[#fff1ea] text-lg font-semibold text-[#7a1d16]"
              >
                !
              </div>
            ) : (
              <div
                aria-hidden="true"
                className="h-12 w-12 shrink-0 animate-spin rounded-full border-[3px] border-neutral-950 border-t-[#d8b35f]"
              />
            )}
            <p className="text-sm leading-relaxed text-neutral-800">
              {setupFailed
                ? setupError
                : "We are creating your campaign and preparing the editor. This should only take a moment."}
            </p>
          </div>
          {setupFailed ? (
            <div className="mt-8 flex flex-wrap items-center gap-3 border-t-2 border-neutral-950/12 pt-6">
              <Button onClick={onRetrySetup} variant="primary" chrome="frame" type="button">
                Retry setup
              </Button>
              {onDismissSetup ? (
                <Button onClick={onDismissSetup} variant="cancel" chrome="frame" type="button">
                  Back to editor
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : view === "choice" ? (
        <div className="flex flex-col">
          <p className={MONOLITH_BOARD_MODAL_EYEBROW_CLASS}>West Marches</p>
          <h2 id="onboarding-title" className={cn("mt-2", MONOLITH_BOARD_MODAL_TITLE_CLASS)}>
            Sign in or create an account
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-neutral-800">
            Save maps to the cloud, manage campaigns, and pick up where you left off.
          </p>
          <div className="mt-8 flex flex-col gap-3 border-t-2 border-neutral-950/12 pt-6">
            <Button
              onClick={() => setView("sign-in")}
              disabled={submitting}
              variant="primary"
              chrome="frame"
              type="button"
            >
              Sign In
            </Button>
            <Button
              onClick={() => setView("sign-up")}
              disabled={submitting}
              variant="secondary"
              chrome="frame"
              type="button"
            >
              Create Account
            </Button>
            <Button onClick={onClose} disabled={submitting} variant="cancel" chrome="frame" type="button">
              Cancel
            </Button>
          </div>
        </div>
      ) : view === "sign-in" ? (
        <div className="flex flex-col">
          <p className={MONOLITH_BOARD_MODAL_EYEBROW_CLASS}>Welcome back</p>
          <h2 id="onboarding-title" className={cn("mt-2", MONOLITH_BOARD_MODAL_TITLE_CLASS)}>
            Sign in
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-neutral-800">
            Enter your email and password to continue.
          </p>
          <div className="mt-6 space-y-4">
            <label className="block">
              <span className={MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS}>Email address</span>
              <input
                type="email"
                value={signInEmail}
                autoFocus
                required
                autoComplete="email"
                onChange={(event) => setSignInEmail(event.target.value)}
                className={MONOLITH_BOARD_MODAL_INPUT_CLASS}
                placeholder="you@example.com"
              />
            </label>
            <label className="block">
              <span className={MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS}>Password</span>
              <input
                type="password"
                value={signInPassword}
                required
                autoComplete="current-password"
                onChange={(event) => setSignInPassword(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void handleSignInSubmit();
                  }
                }}
                className={MONOLITH_BOARD_MODAL_INPUT_CLASS}
                placeholder="Your password"
              />
            </label>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3 border-t-2 border-neutral-950/12 pt-6">
            <Button
              onClick={() => setView("choice")}
              disabled={submitting}
              variant="cancel"
              chrome="frame"
              type="button"
            >
              Back
            </Button>
            <Button
              onClick={() => void handleSignInSubmit()}
              disabled={!canSubmitSignIn}
              variant="primary"
              chrome="frame"
              type="button"
            >
              {submitting ? "Signing in…" : "Sign In"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <MonolithBoardModalSteps
            steps={modalSteps}
            stepNumber={stepNumber}
            stepCount={SIGN_UP_STEPS.length}
            currentStepLabel={currentStep ? STEP_LABELS[currentStep.key] : ""}
            disabled={submitting}
          />

          {currentStep ? (
            <div className="mt-6">
              <h2
                id="onboarding-title"
                className={cn("text-lg font-black uppercase tracking-[-0.04em] text-neutral-950 sm:text-xl")}
              >
                {currentStep.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-neutral-800">{currentStep.description}</p>

              {isAccountStep ? (
                <div className="mt-6 space-y-4">
                  <label className="block">
                    <span className={MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS}>Email address</span>
                    <input
                      type="email"
                      value={values.email}
                      autoFocus
                      required
                      autoComplete="email"
                      onChange={(event) =>
                        setValues((current) => ({ ...current, email: event.target.value }))
                      }
                      className={MONOLITH_BOARD_MODAL_INPUT_CLASS}
                      placeholder="you@example.com"
                    />
                  </label>
                  <label className="block">
                    <span className={MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS}>Password</span>
                    <input
                      type="password"
                      value={values.password}
                      required
                      minLength={6}
                      autoComplete="new-password"
                      onChange={(event) =>
                        setValues((current) => ({ ...current, password: event.target.value }))
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          void handleSignUpPrimaryAction();
                        }
                      }}
                      className={MONOLITH_BOARD_MODAL_INPUT_CLASS}
                      placeholder="At least 6 characters"
                    />
                  </label>
                </div>
              ) : (
                <label className="mt-6 block">
                  <span className={MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS}>{currentStep.label}</span>
                  <input
                    type="text"
                    value={values[currentStep.key]}
                    autoFocus
                    required
                    autoComplete="off"
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        [currentStep.key]: event.target.value,
                      }))
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void handleSignUpPrimaryAction();
                      }
                    }}
                    className={MONOLITH_BOARD_MODAL_INPUT_CLASS}
                    placeholder={currentStep.placeholder}
                  />
                </label>
              )}
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap items-center gap-3 border-t-2 border-neutral-950/12 pt-6">
            {stepIndex > 0 ? (
              <Button
                onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
                disabled={submitting}
                variant="cancel"
                chrome="frame"
                type="button"
              >
                Back
              </Button>
            ) : (
              <Button
                onClick={() => setView("choice")}
                disabled={submitting}
                variant="cancel"
                chrome="frame"
                type="button"
              >
                Back
              </Button>
            )}

            <Button
              onClick={() => void handleSignUpPrimaryAction()}
              disabled={!canContinueSignUp || submitting}
              variant="primary"
              chrome="frame"
              type="button"
            >
              {submitting
                ? "Creating account..."
                : stepIndex === SIGN_UP_STEPS.length - 1
                  ? "Create account"
                  : "Continue"}
            </Button>
          </div>
        </>
      )}
    </MonolithBoardModal>
  );
}
